/**
 * Course Service - Handles course-related API calls
 */
import { apiClient, API_ENDPOINTS, withErrorHandling } from '../api/index.js';

class CourseService {
  constructor() {
    this.apiClient = apiClient;
  }

  /**
   * Fetch all purchased courses for a user
   * @param {string} userId - The ID of the user
   * @returns {Promise} Service response with purchased courses data
   */
  /**
   * Fetch course details by ID
   * @param {string} courseId - The ID of the course to fetch
   * @returns {Promise} Service response with course data
   */
  async getCourseById(courseId) {
    // If courseId is null or undefined, return all active courses
    if (courseId === null || courseId === undefined) {
      return this.getCourses();
    }
    
    console.log('Fetching course details for ID:', courseId);
    
    // First, get the topic details to find the courseId
    try {
      if (!this.apiClient.isInitialized) {
        await this.apiClient.initialize();
      }
      
      // First, check if this is a topic ID by fetching topic details
      let topicResponse;
      try {
        topicResponse = await this.apiClient.get(`/topics/${courseId}`);
        console.log('Topic details response:', topicResponse);
        
        // If we get here, it's a topic ID, so get the course using the courseId from topic
        if (topicResponse.data && topicResponse.data.courseId) {
          const courseResponse = await this.apiClient.get(`/courses/getAll?courseId=${topicResponse.data.courseId}`);
          console.log('Course details response:', courseResponse);
          return Array.isArray(courseResponse.data) ? courseResponse.data[0] : courseResponse.data;
        }
      } catch (topicError) {
        console.log('Not a topic ID or error fetching topic, trying as course ID');
      }
      
      // If we're here, it's not a topic ID or we couldn't find the topic
      const courseResponse = await this.apiClient.get(`/courses/getAll?courseId=${courseId}`);
      console.log('Course details response (direct course ID):', courseResponse);
      return Array.isArray(courseResponse.data) ? courseResponse.data[0] : courseResponse.data;
    } catch (error) {
      console.error('Error fetching course details:', error);
      throw error;
    }
  }

  /**
   * Fetch all purchased courses for a user
   * @param {string} userId - The ID of the user
   * @returns {Promise} Service response with purchased courses data
   */
  async getPurchasedCourses(userId) {
    console.log('Fetching purchased courses for user:', userId);
    const endpoint = API_ENDPOINTS.purchases.list(userId);
    console.log('API Endpoint:', endpoint);
    
    try {
      // Ensure API client is initialized
      if (!this.apiClient.isInitialized) {
        console.log('Initializing API client...');
        await this.apiClient.initialize();
      }
      
      console.log('Making authenticated request with token...');
      const response = await this.apiClient.get(endpoint);
      console.log('Purchased courses raw response:', response);
      
      // Check if response.data exists and is an array
      if (!response || !response.data || !Array.isArray(response.data)) {
        console.error('Invalid response format:', response);
        return [];
      }
      
      console.log('Number of purchases found:', response.data.length);

      // Process all purchases
      const directPurchases = await Promise.all(
        response.data.map(async (purchase) => {
            try {
              const course = await this.getCourseById(purchase.courseId);
              if (!course) return null;
              
              return {
                ...course,
                id: course.id,
                courseId: course.id,
                purchaseId: purchase.id,
                purchaseDate: purchase.purchaseDate,
                accessExpiryDate: purchase.accessExpiryDate,
                pricePaid: purchase.pricePaid,
                originalPrice: purchase.originalPrice,
                title: course.title || `Course ${course.id}`,
                description: course.description || '',
                image: course.image || '/default-course.jpg'
              };
            } catch (error) {
              console.error(`Error processing purchase ${purchase.id}:`, error);
              return null;
            }
          })
      );
      
      // Filter out any null values from failed fetches
      const validPurchases = directPurchases.filter(Boolean);
      console.log('Valid purchased courses:', validPurchases);
      
      return validPurchases;
    } catch (error) {
      console.error('Error in getPurchasedCourses:', error);
      
      // Handle unauthorized error specifically
      if (error.response?.status === 401) {
        console.error('Authentication failed. Please log in again.');
        // You might want to trigger a logout or redirect to login here
        // window.location.href = '/login';
      }
      
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
      } else if (error.request) {
        console.error('No response received:', error.request);
      } else {
        console.error('Error message:', error.message);
      }
      
      throw error;
    }
  }

  /**
   * Fetch all courses
   * @returns {Promise} Service response with courses data
   */
  async getCourses() {
    return await withErrorHandling(
      async () => {
        const response = await this.apiClient.get(`${API_ENDPOINTS.content.courses}?status=ACTIVE`);
        return response.data;
      },
      { operation: 'getCourses' }
    );
  }

  /**
   * Fetch a single course by ID
   * @param {string} courseId - The ID of the course to fetch
   * @returns {Promise} Service response with course data
   */
  async getCourseById(courseId) {
    return await withErrorHandling(
      async () => {
        if (courseId === null || courseId === undefined) {
          const response = await this.apiClient.get(`${API_ENDPOINTS.content.courses}?status=ACTIVE`);
          return response.data;
        }
        const response = await this.apiClient.get(
          API_ENDPOINTS.content.courseById(courseId)
        );
        return response.data;
      },
      { operation: 'getCourseById', courseId }
    );
  }

  /**
   * Fetch course details with curriculum/subjects
   * @param {string} courseId - The ID of the course to fetch
   * @returns {Promise} Service response with detailed course data
   */
  async getCourseDetails(courseId) {
    return await withErrorHandling(
      async () => {
        const response = await this.apiClient.get(
          API_ENDPOINTS.content.courseById(courseId)
        );
        return response.data;
      },
      { operation: 'getCourseDetails', courseId }
    );
  }
}

// Create a singleton instance
export const courseService = new CourseService();

export default courseService;
